/**
 * useCountdownPress — le minuteur de `<CountdownButton>` : il presse le bouton au bout de
 * `durationMs`, une fois que le compte à rebours court, et le geste de l'élève passe avant.
 *
 * Une seule pression, quelle qu'en soit la source : le geste et l'échéance arrivent souvent ensemble
 * (un toucher dans la dernière fraction de seconde), et la suite ne doit partir qu'une fois. Un
 * compte à rebours arrêté (`running` faux) repart de zéro quand il reprend : le bouton pas encore
 * montré, ou les félicitations du live sous la liste des activités ouverte.
 */
import { useCallback, useEffect, useRef } from "react"

export type CountdownPressOptions = {
    durationMs: number
    /** Le compte à rebours court. */
    running: boolean
    onPress: () => void
}

/** Rend la pression gardée : à brancher sur le geste du bouton. */
export function useCountdownPress(options: CountdownPressOptions): () => void {
    const { durationMs, running, onPress } = options
    const pressed = useRef(false)
    const latest = useRef(onPress)
    latest.current = onPress

    const press = useCallback(() => {
        if (pressed.current) return
        pressed.current = true
        latest.current()
    }, [])

    useEffect(() => {
        if (!running) return
        const timer = setTimeout(press, Math.max(0, durationMs))
        return () => clearTimeout(timer)
    }, [running, durationMs, press])

    return press
}
